/* eslint-disable import/prefer-default-export */
export const determineIsOnSale = (collection, priceZone) => {
  const listMax = collection?.[`zone_${priceZone}_list_price_max`]
  const listMin = collection?.[`zone_${priceZone}_list_price_min`]
  const saleMax = collection?.[`zone_${priceZone}_sale_price_max`]
  const saleMin = collection?.[`zone_${priceZone}_sale_price_min`]

  if (!listMax || !listMin || !saleMax || !saleMin) {
    return false
  }

  return saleMin < listMin || saleMax < listMax
}

/**
 * Determines the image gallery images for a given variation
 * @param collection - The collection object.
 * @param featuredSlots - Search Query specified featured slots (e.g. ['bunk bed', 'loft bed'])
 * @param swatchLabel - The swatch label (e.g. 'white').
 * @returns [images, hasFeaturedImages]
 */
const determineVariationImages = (collection, featuredSlots, swatchLabel) => {
  const { variations, roomVariationSlots } = collection ?? {}
  const variation = variations?.[swatchLabel]

  // First, we prioritize images using the featured slots
  // We'll see these images first
  const featuredImages =
    featuredSlots
      ?.map(slot => {
        const roomImage = roomVariationSlots?.[swatchLabel]?.[slot]?.roomImage
        const sameVariation = roomVariationSlots?.[swatchLabel]?.[slot]?.sameVariation
        if (!roomImage || !sameVariation) return null
        return roomImage
      })
      .filter(Boolean) ?? []

  // Then we show the remaining room images for the variation
  const { roomImages: baseRoomImages } = variation ?? {}

  const roomImages = baseRoomImages ?? []

  // The room images may have contained the featured images, so we remove any duplicates
  const imageSet = new Set([...featuredImages, ...roomImages])

  // And return the images as an array
  return [Array.from(imageSet), !!featuredImages.length]
}

/**
 * Determines the swatch data for a given collection.
 * @param collection - The collection object.
 * @param featuredSlots - Search Query specified featured slots (e.g. ['bunk bed', 'loft bed'])
 * @returns An array of swatch data objects.  Priority is given to swatches containing featured images.
 * @example
 * {
 *   image: 'https://via.placeholder.com/150',
 *   label: 'white',
 *   images: ['https://via.placeholder.com/150', 'https://via.placeholder.com/151'],
 *   hasFeaturedImages: true,
 *   startIndex: 28,
 *   length: 10,
 * }
 */
export const determineSwatchData = (collection, featuredSlots) =>
  Object.entries(collection.variations)
    ?.map(([swatchLabel, value]) => {
      const swatchImage = value?.swatch_image

      const [images, hasFeaturedImages] = determineVariationImages(collection, featuredSlots, swatchLabel)

      return {
        image: swatchImage,
        label: swatchLabel,
        length: images.length,
        images,
        hasFeaturedImages,
      }
    })
    .filter(Boolean)
    .sort((a, b) => b.hasFeaturedImages - a.hasFeaturedImages)
    .reduce((acc, curr, index) => {
      // Calculate the starting index based on previous items' lengths
      const startIndex = index === 0 ? 0 : acc[index - 1].startIndex + acc[index - 1].length

      return [
        ...acc,
        {
          ...curr,
          startIndex,
        },
      ]
    }, [])
    .filter(Boolean)
